import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Flame, ShieldCheck, PackageCheck } from "lucide-react";
import axios from "axios";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";

const API = process.env.REACT_APP_BACKEND_URL;
const COOKING_HERO = "/products/cooking/731077_fe46acfa02dd4e85ac22a993ccb6b8eb~mv2.jpg";
const fmt = (n) => n.toLocaleString("en-NZ", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const WHY = [
  { icon: Flame, t: "Real South African Braai Heritage", d: "CADAC's 70-year Skottel legacy and Quick Pitch's 3CR12 stainless build — not flimsy imported flatpack barbecues." },
  { icon: ShieldCheck, t: "Built For Constant Outdoor Use", d: "GreenGrill ceramic coatings, piezo ignition and marine-grade stainless — cook properly on every trip, not just the odd weekend." },
  { icon: PackageCheck, t: "Portable By Design", d: "Fold-flat legs, carry bags and compact footprints so cooking gear doesn't eat your storage space." },
];

const priceLabel = (m) => `${m.priceFrom ? "FROM " : ""}$${fmt(m.price)}`;

// Native home is now Unique Camping Gear (/camping-accessories) — cross-linked here since it's
// still one of our most popular portable cooking items.
const PROMOTED_PRODUCTS = [
  { name: "Quick Barbe / Braai", price: 445.0, priceFrom: true, tagline: "3CR12 Stainless Portable Folding Braai", thumb: "/products/cooking/731077_43138a0e398d404e85ccb701bdda6c7d~mv2.jpg", to: "/camping-accessories/quick-barbe" },
];

const Card = ({ m, testid }) => (
  <Link
    to={m.to || `/camp-cooking/${m.slug}`}
    data-testid={testid}
    className="group relative flex flex-col overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] overflow-hidden relative bg-white">
      <HoverImage primary={m.thumb} secondary={m.gallery?.[1]?.src} alt={m.name} className="w-full h-full object-contain p-6" />
      {m.badge && (
        <div className="absolute top-4 left-4">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">{m.badge}</span>
        </div>
      )}
    </div>
    <div className="p-5 md:p-6 border-t border-[var(--border-default)] flex-1 flex flex-col">
      <h3 className="font-display text-lg md:text-xl text-white leading-tight tracking-wide">{m.name.toUpperCase()}</h3>
      <p className="text-[11px] font-mono uppercase tracking-wider text-white/60 mt-2 line-clamp-1">{m.tagline}</p>
      <div className="flex items-end justify-between gap-3 mt-4 pt-4 border-t border-[var(--border-default)]">
        <div>
          {m.priceWas && <p className="font-mono text-[10px] text-white/40 line-through">${fmt(m.priceWas)}</p>}
          <p className="font-display text-xl text-[var(--brand-primary)] leading-none">{priceLabel(m)}</p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
          View <ArrowUpRight size={12} />
        </span>
      </div>
    </div>
  </Link>
);

export default function CookingPage() {
  const [products, setProducts] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/cooking`).then((res) => setProducts(res.data)).catch((err) => { console.error(err); setProducts([]); });
  }, []);

  if (products === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  return (
    <div data-testid="cooking-page">
      <SEO
        path="/camp-cooking"
        title="Camp & Portable Cooking NZ · CADAC & Quick Pitch"
        description="CADAC gas barbecues, the Skottel Braai, and the 3CR12 stainless Quick Barbe — not your average camp cookers. From $79 to $499."
        image={COOKING_HERO}
      />

      <section className="relative min-h-[80svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={COOKING_HERO} alt="Portable camp cooking gear" className="w-full h-full object-cover" style={{ objectPosition: "50% 40%" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-12% via-black/20 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-20 pt-32 w-full">
          <p className="overline mb-6">— Camp & Portable Cooking</p>
          <h1 className="font-display text-[11vw] md:text-[8vw] lg:text-[6.5vw] leading-[0.88] text-white hero-text-shadow max-w-[18ch]">
            NOT YOUR AVERAGE<br />
            <span className="text-[var(--brand-primary)]">CAMP COOKER.</span>
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
            <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/85 leading-relaxed max-w-xl [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
              CADAC gas barbecues built on 70 years of South African braai heritage, plus the 3CR12 stainless Quick Barbe — gear that actually holds up to real outdoor use.
            </p>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <a href="#range" data-testid="cooking-cta-range" className="btn-primary">View Range <ArrowUpRight size={14} /></a>
              <a href="#enquire" className="btn-secondary">Enquire</a>
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {WHY.map((w, i) => {
            const Icon = w.icon;
            return (
              <div key={i} data-testid={`cooking-why-${i}`} className="bg-[var(--bg-main)] p-6 md:p-8">
                <Icon size={22} className="text-[var(--brand-primary)] mb-4" strokeWidth={1.6} />
                <h3 className="font-display text-lg md:text-xl text-white mb-2">{w.t}</h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{w.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section id="range" className="container-x pb-16 md:pb-20 scroll-mt-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline mb-3">— The Range</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              GAS BBQS & <span className="text-[var(--brand-primary)]">BRAAIS.</span>
            </h2>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)]">Prices GST inc.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((m, i) => <Card key={m.slug} m={m} testid={`cooking-item-${i}`} />)}
          {PROMOTED_PRODUCTS.map((m, i) => <Card key={m.to} m={m} testid={`cooking-promoted-${i}`} />)}
        </div>
      </section>
    </div>
  );
}
